import {
  resolveDeviceAnalyticsContext,
  type DeviceAnalyticsContext
} from '#~/analytics/device-context.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { useAnalytics } from '#~/analytics/index.js';
import {
  useAudioDeviceStats,
  useVideoDeviceStats,
  type DeviceStats
} from '#~/agent/components/monitoring/summary/device-stats-hook.js';
import { locateDevice } from '#~/room/index.js';
import { useMultiDeviceStateQuery, useMultiProxyStateQuery } from '#~/statequery/hook.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isTransient, type Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import {
  StatusDeviceConnectionStatus,
  StatusStreamState
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef } from 'react';

type MediaChannel = 'video' | 'audio';

type DeviceLifecycleSnapshot = {
  videoConnectionStatus: StatusDeviceConnectionStatus | undefined;
  audioConnectionStatus: StatusDeviceConnectionStatus | undefined;
  controlsConnectionStatus: StatusDeviceConnectionStatus | undefined;
  videoStreamState: StatusStreamState | undefined;
  audioStreamState: StatusStreamState | undefined;
};

type ConnectionStateRef = {
  video: StatusDeviceConnectionStatus | undefined;
  audio: StatusDeviceConnectionStatus | undefined;
  controls: StatusDeviceConnectionStatus | undefined;
};

type StreamStateRef = {
  video: StatusStreamState | undefined;
  audio: StatusStreamState | undefined;
};

const QUALITY_PING_DELAY_MS = 30_000;
const QUALITY_PING_INTERVAL_MS = 300_000;
const ROOM_LIFECYCLE_SURFACE = 'room_lifecycle';

const isConnected = (status?: StatusDeviceConnectionStatus): boolean =>
  status === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

const isStreamingReady = (state?: StatusStreamState): boolean =>
  state === StatusStreamState.STREAM_STATE_READY ||
  state === StatusStreamState.STREAM_STATE_PLAYING;

// Per §1.3.5 / §B item 9 (ENG-3960): a "permanent" error is a terminal failure the
// device cannot recover from without operator action — it STOPS the quality ping and
// is the trigger for a *_error event even when the stream never reached ready. A
// transient not-ready state (CONNECTING / ASYNC / NO_PREROLL / PAUSED) keeps the ping
// alive with is_error_state set, so we do not lose visibility during recovery blips.
const isConnectionError = (status?: StatusDeviceConnectionStatus): boolean =>
  status === StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

const isStreamError = (state?: StatusStreamState): boolean =>
  state === StatusStreamState.STREAM_STATE_FAIL || state === StatusStreamState.STREAM_STATE_NULL;

// error_type tokens are stable cohort keys (queryable in PostHog); error_message carries
// the raw proto enum label for human triage. Per rule: think-in-systems — one derivation,
// reused by every error event + the error-state ping.
const connectionErrorType = (status?: StatusDeviceConnectionStatus): string =>
  isConnectionError(status) ? 'connection_failed' : 'disconnected';

const streamErrorType = (state?: StatusStreamState): string =>
  isStreamError(state) ? 'stream_failed' : 'stream_not_ready';

const connectionStatusLabel = (status?: StatusDeviceConnectionStatus): string =>
  status === undefined
    ? 'unknown'
    : (StatusDeviceConnectionStatus[status] ?? `connection_status_${status}`);

const streamStateLabel = (state?: StatusStreamState): string =>
  state === undefined ? 'unknown' : (StatusStreamState[state] ?? `stream_state_${state}`);

// Whichever side is unhealthy decides the error_type: a down connection dominates a
// not-ready stream (you cannot stream over a dead connection).
const pingErrorType = (status?: StatusDeviceConnectionStatus, state?: StatusStreamState): string =>
  !isConnected(status) ? connectionErrorType(status) : streamErrorType(state);

const pingErrorMessage = (
  status?: StatusDeviceConnectionStatus,
  state?: StatusStreamState
): string => (!isConnected(status) ? connectionStatusLabel(status) : streamStateLabel(state));

// connection_status is an optional analytics prop; under exactOptionalPropertyTypes we
// must OMIT it rather than emit an explicit `undefined`, so spread this builder instead
// of assigning the field directly.
const connectionStatusProp = (status?: StatusDeviceConnectionStatus): Record<string, number> =>
  status === undefined ? {} : { connection_status: status };

// --- Per-device quality-ping timer registry ----------------------------------------
//
// §1.3.5 requires a SINGLE per-device timer keyed by device id — NOT a module-global
// timer (that would conflate devices) and NOT a per-React-instance timer (a re-mount or
// a second observer instance would spawn a duplicate timer and double-fire the ping).
// We therefore key the timer by device id in a module-level Map with refcounting, the
// same convention device-stats-hook.ts uses for its per-device stats managers
// (per rule: investigate-before-adding — reuse the established pattern). Multiple
// subscribers for one device share ONE timer; teardown happens when the last releases.
type QualityPingController = {
  refCount: number;
  started: boolean;
  timeout: ReturnType<typeof setTimeout> | null;
  interval: ReturnType<typeof setInterval> | null;
  // Latest emit closure, refreshed by the owning component so the module-level timer
  // always fires against fresh React state (current stats / context / error flags).
  emit: (() => void) | null;
};

const qualityPingControllers = new Map<Device['id'], QualityPingController>();

const acquireQualityPingController = (deviceId: Device['id']): QualityPingController => {
  let controller = qualityPingControllers.get(deviceId);

  if (!controller) {
    controller = { refCount: 0, started: false, timeout: null, interval: null, emit: null };
    qualityPingControllers.set(deviceId, controller);
  }

  controller.refCount += 1;

  return controller;
};

const stopQualityPingTimer = (controller: QualityPingController): void => {
  if (controller.timeout !== null) {
    clearTimeout(controller.timeout);
    controller.timeout = null;
  }

  if (controller.interval !== null) {
    clearInterval(controller.interval);
    controller.interval = null;
  }

  controller.started = false;
};

const startQualityPingTimer = (controller: QualityPingController): void => {
  // Idempotent: a no-op while a timer is already live so transitions that re-assert
  // "monitoring" never reset the 30s/5min cadence.
  if (controller.started) {
    return;
  }

  controller.started = true;
  // First ping fires 30s after streaming_success, then every 5 minutes.
  controller.timeout = setTimeout(() => {
    controller.emit?.();
    controller.interval = setInterval(() => {
      controller.emit?.();
    }, QUALITY_PING_INTERVAL_MS);
    controller.timeout = null;
  }, QUALITY_PING_DELAY_MS);
};

const releaseQualityPingController = (deviceId: Device['id']): void => {
  const controller = qualityPingControllers.get(deviceId);

  if (!controller) {
    return;
  }

  controller.refCount -= 1;

  if (controller.refCount <= 0) {
    stopQualityPingTimer(controller);
    controller.emit = null;
    qualityPingControllers.delete(deviceId);
  }
};

/**
 * @internal Test-only: clear and drop every per-device ping timer so module-level
 * state never leaks between Jest cases. Mirrors resetDeviceStatsManagers().
 */
export const resetMediaQualityPingTimers = (): void => {
  for (const controller of qualityPingControllers.values()) {
    stopQualityPingTimer(controller);
  }

  qualityPingControllers.clear();
};

type SourceMetadata = Record<string, unknown>;

const resolveSourceMetadata = (
  deviceId: Device['id'],
  mediaType: 'video' | 'audio' | 'controls'
): SourceMetadata => {
  const device = locateDevice(deviceId);

  if (!device) {
    return {};
  }

  const result: SourceMetadata = {};

  if (mediaType === 'video' && device.video?.config) {
    const cfg = device.video.config;
    result.source_protocol = cfg.protocol;

    if ('host' in cfg && cfg.host) {
      const url = cfg.host + ('path' in cfg && cfg.path ? `/${cfg.path}` : '');
      result.source_url = url;
    }

    if ('port' in cfg && cfg.port) {
      result.source_port = cfg.port;
    }
  } else if (mediaType === 'controls' && device.control?.config) {
    const cfg = device.control.config;
    result.source_protocol = cfg.protocol;

    if ('host' in cfg && cfg.host) {
      result.source_url = cfg.host;
    }

    if ('port' in cfg && cfg.port) {
      result.source_port = cfg.port;
    }
  }

  return result;
};

// Downstream (LiveKit JS SDK) stats — the tray's "Receiving" cells. Every field here,
// including quality_score, is best-effort: room-track-stats.ts only computes a value
// once 2 RTC-stats samples exist for the CURRENT track, while this ping fires on its
// own 30s/5min timer with no coordination with that poller. A track swap (reconnect,
// renegotiation) resets the sample history, so a ping can legitimately land mid-reset.
// canonical-event-map.json does NOT require quality_score for this reason —
// holding the ping until the stat exists would drop the heartbeat during
// exactly the reconnect windows where visibility matters most (§1.3.5).
const resolveStreamingStats = (stats: DeviceStats): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (stats.bitrate !== null) {
    result.bitrate = Math.round(stats.bitrate);
  }

  if (stats.width !== null && stats.height !== null) {
    result.resolution = `${stats.width}x${stats.height}`;
  }

  if (stats.framesPerSecond !== null) {
    result.fps = stats.framesPerSecond;
  }

  if (stats.quality !== null) {
    result.quality_score = stats.quality;
  }

  if (stats.codec !== null && stats.codec !== undefined) {
    result.codec = stats.codec;
  }

  if (stats.packetLoss !== null) {
    result.packet_loss = stats.packetLoss;
  }

  return result;
};

// Upstream (bridge → cloud) stats — the tray's "Sending" cells, sourced from proxy
// device status the same way monitoring/summary.tsx renders them. Prefixed upstream_*
// so a quality ping carries BOTH legs of the path (match the tray, §1.3.5).
type UpstreamChannelStats = {
  bps?: number;
  framerate?: number;
  network?: number;
  width?: number;
  height?: number;
  codec?: string;
};

type UpstreamProxyEntry = {
  found: boolean;
  video?: UpstreamChannelStats;
  audio?: UpstreamChannelStats;
};

const resolveUpstreamStats = (
  channel: MediaChannel,
  upstream: UpstreamProxyEntry | undefined
): Record<string, unknown> => {
  const stats = upstream?.[channel];

  if (!stats) {
    return {};
  }

  const result: Record<string, unknown> = {};

  if (stats.bps !== undefined && stats.bps !== null) {
    result.upstream_bitrate = Math.round(stats.bps);
  }

  if (stats.framerate !== undefined && stats.framerate !== null) {
    result.upstream_fps = stats.framerate;
  }

  if (stats.network !== undefined && stats.network !== null) {
    // The tray renders network*5 as a 0-5 "Quality Score"; we ship the raw 0-1 value.
    result.upstream_quality_score = stats.network;
  }

  if (
    stats.width !== undefined &&
    stats.width !== null &&
    stats.height !== undefined &&
    stats.height !== null
  ) {
    result.upstream_resolution = `${stats.width}x${stats.height}`;
  }

  if (stats.codec !== undefined && stats.codec !== null) {
    result.upstream_codec = stats.codec;
  }

  return result;
};

const DeviceLifecycleAnalyticsObserverEntry = ({
  deviceId,
  roomId
}: {
  deviceId: Device['id'];
  roomId: Room['id'];
}): ReactElement | null => {
  const { track } = useAnalytics();
  const mediaControlsDiscoveryTracker = useMemo(
    () => createMediaControlsDiscoveryTracker(track),
    [track]
  );
  const deviceMap = useMultiDeviceStateQuery<DeviceLifecycleSnapshot | undefined>({
    minInterval: 500,
    // The transform returns the PER-DEVICE value — multiDeviceStateQuery keys
    // the result map by device id itself. Wrapping the value in a record makes
    // every snapshot field read as undefined downstream.
    transform: (device) =>
      device.id === deviceId
        ? {
            videoConnectionStatus: device.status?.video?.connectionStatus?.status,
            audioConnectionStatus: device.status?.audio?.connectionStatus?.status,
            controlsConnectionStatus: device.status?.movement?.connectionStatus?.status,
            videoStreamState: device.status?.video?.status?.state,
            audioStreamState: device.status?.audio?.status?.state
          }
        : undefined
  });
  const snapshot = deviceMap[deviceId];
  const videoStats = useVideoDeviceStats(roomId, deviceId, 500);
  const audioStats = useAudioDeviceStats(roomId, deviceId, 500);
  // Upstream (sending) stats from proxy device status — mirrors monitoring/summary.tsx.
  const upstreamStatsMap = useMultiProxyStateQuery<UpstreamProxyEntry>((state) => {
    const device = state?.status?.device?.[deviceId];
    const video = device?.video?.status;
    const audio = device?.audio?.status;

    return {
      found: !!device,
      video: video
        ? {
            bps: video.bps,
            framerate: video.framerate,
            network: video.network ?? device?.video?.localStatus?.network,
            width: video.width,
            height: video.height
          }
        : undefined,
      audio: audio
        ? {
            bps: audio.bps,
            codec: audio.codec
          }
        : undefined
    };
  });
  const upstreamStats = Object.values(upstreamStatsMap).find((entry) => entry.found);

  const initializedRef = useRef(false);
  const previousConnectionRef = useRef<ConnectionStateRef>({
    video: undefined,
    audio: undefined,
    controls: undefined
  });
  const previousStreamRef = useRef<StreamStateRef>({
    video: undefined,
    audio: undefined
  });
  const lastKnownContextRef = useRef<DeviceAnalyticsContext | undefined>(undefined);
  const videoConnectFiredRef = useRef(false);
  const audioConnectFiredRef = useRef(false);
  const controlsConnectFiredRef = useRef(false);
  // ENG-4313 first-stream dedupe. Mirrors the connect-fired refs above: once the CANONICAL
  // media_stream_succeeded has fired for a channel, subsequent not-ready→ready reconnect
  // blips must NOT re-fire it (unguarded streaming-success re-firing on every blip was the
  // over-count root cause). Reset only on device removal — a transient blip is the same
  // stream, so it stays guarded; a re-added device is a fresh first stream. Reconnect
  // diagnostics and quality-ping arming remain independent of the retired alias.
  const videoStreamSucceededFiredRef = useRef(false);
  const audioStreamSucceededFiredRef = useRef(false);
  // Whether each media channel is inside its "monitoring window": from the first
  // streaming_success until device removal or a permanent error. Drives which channels
  // the per-device timer pings on each tick, and whether the timer runs at all.
  const monitoredRef = useRef<Record<MediaChannel, boolean>>({ video: false, audio: false });
  const controllerRef = useRef<QualityPingController | null>(null);

  // Build one ping per channel still inside its monitoring window. Fires EVEN in
  // (transient) error states with is_error_state + error_type set (§1.3.5), so we keep
  // visibility through recovery blips. Permanent-error / removed channels are dropped
  // from monitoredRef before this runs, so they are silently skipped.
  const emitQualityPings = useLatestFn((): void => {
    const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId, {
      includeUptime: true
    });

    if (!deviceAnalyticsContext) {
      return;
    }

    const channels: MediaChannel[] = ['video', 'audio'];

    for (const channel of channels) {
      if (!monitoredRef.current[channel]) {
        continue;
      }

      const connectionStatus =
        channel === 'video' ? snapshot?.videoConnectionStatus : snapshot?.audioConnectionStatus;
      const streamState =
        channel === 'video' ? snapshot?.videoStreamState : snapshot?.audioStreamState;
      const downstreamStats = channel === 'video' ? videoStats : audioStats;
      const isErrorState = !(isConnected(connectionStatus) && isStreamingReady(streamState));

      mediaControlsDiscoveryTracker.trackMediaQualityPing({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: channel,
        ...connectionStatusProp(connectionStatus),
        is_error_state: isErrorState,
        // Carry error context whenever the ping is fired in a degraded state.
        ...(isErrorState
          ? {
              error_type: pingErrorType(connectionStatus, streamState),
              error_message: pingErrorMessage(connectionStatus, streamState)
            }
          : {}),
        // Downstream (LiveKit) + upstream (proxy) tray stats — match the monitoring tray.
        ...resolveStreamingStats(downstreamStats),
        ...resolveUpstreamStats(channel, upstreamStats)
      });
    }
  });

  // Acquire the per-device timer once on mount; release on unmount. The stable
  // emitQualityPings wrapper (useLatestFn) is what the module-level timer invokes.
  useEffect(() => {
    const controller = acquireQualityPingController(deviceId);
    controller.emit = emitQualityPings;
    controllerRef.current = controller;

    return () => {
      controllerRef.current = null;
      releaseQualityPingController(deviceId);
    };
  }, [deviceId, emitQualityPings]);

  useEffect(() => {
    const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId, {
      includeUptime: true
    });

    if (deviceAnalyticsContext) {
      lastKnownContextRef.current = deviceAnalyticsContext;
    }

    // ENG-4313: emit media_stream_succeeded through the first-stream dedupe. The per-channel
    // fired-ref suppresses the CANONICAL re-emit on reconnect blips within this mount; the
    // tracker's session-scoped dedupe key keeps it once-per-committed-device even across
    // observer unmount/remount (the fired-ref resets on remount). B1 fix: this room observer
    // is the SINGLE canonical source — the add_device/preview surface (discovery.tsx) no
    // longer mints the canonical because its transient preview id never matches this
    // committed id. Reconnects still re-arm the quality-ping monitoring window without
    // sending another first-stream row.
    const emitStreamingSuccess = (
      channel: MediaChannel,
      streamingProperties: EventProperties
    ): void => {
      const firedRef =
        channel === 'video' ? videoStreamSucceededFiredRef : audioStreamSucceededFiredRef;
      mediaControlsDiscoveryTracker.trackMediaStreamingSuccess(streamingProperties, {
        emitCanonical: !firedRef.current,
        canonicalDedupeKey: `${deviceId}:${channel}`
      });
      firedRef.current = true;
    };

    if (!snapshot || !deviceAnalyticsContext) {
      const cachedContext = lastKnownContextRef.current;

      if (initializedRef.current && cachedContext) {
        const prevConn = previousConnectionRef.current;
        const prevStream = previousStreamRef.current;

        // Device removal: anything that was up is now (implicitly) an error transition.
        if (isConnected(prevConn.video)) {
          mediaControlsDiscoveryTracker.trackMediaConnectError({
            ...cachedContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'video',
            is_error_state: true,
            error_type: 'disconnected',
            error_message: 'device_removed'
          });
        }

        if (isConnected(prevConn.audio)) {
          mediaControlsDiscoveryTracker.trackMediaConnectError({
            ...cachedContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'audio',
            is_error_state: true,
            error_type: 'disconnected',
            error_message: 'device_removed'
          });
        }

        if (isConnected(prevConn.controls)) {
          mediaControlsDiscoveryTracker.trackControlsConnectError({
            ...cachedContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'controls',
            is_error_state: true,
            error_type: 'disconnected',
            error_message: 'device_removed',
            // Per §1.4.2: did controls_connect_success fire before this error attempt?
            prior_success: controlsConnectFiredRef.current
          });
        }

        if (isStreamingReady(prevStream.video)) {
          mediaControlsDiscoveryTracker.trackMediaStreamingError({
            ...cachedContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'video',
            is_error_state: true,
            error_type: 'stream_not_ready',
            error_message: 'device_removed'
          });
        }

        if (isStreamingReady(prevStream.audio)) {
          mediaControlsDiscoveryTracker.trackMediaStreamingError({
            ...cachedContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'audio',
            is_error_state: true,
            error_type: 'stream_not_ready',
            error_message: 'device_removed'
          });
        }
      }

      // Device gone → stop the ping timer and reset all per-device tracking state.
      if (controllerRef.current) {
        stopQualityPingTimer(controllerRef.current);
      }

      monitoredRef.current = { video: false, audio: false };
      previousConnectionRef.current = { video: undefined, audio: undefined, controls: undefined };
      previousStreamRef.current = { video: undefined, audio: undefined };
      videoConnectFiredRef.current = false;
      audioConnectFiredRef.current = false;
      controlsConnectFiredRef.current = false;
      // A removed device is a completed lifecycle; a later re-add is a fresh first stream.
      videoStreamSucceededFiredRef.current = false;
      audioStreamSucceededFiredRef.current = false;
      initializedRef.current = false;
      return;
    }

    if (!initializedRef.current) {
      previousConnectionRef.current = {
        video: snapshot.videoConnectionStatus,
        audio: snapshot.audioConnectionStatus,
        controls: snapshot.controlsConnectionStatus
      };
      previousStreamRef.current = {
        video: snapshot.videoStreamState,
        audio: snapshot.audioStreamState
      };
      initializedRef.current = true;

      const device = locateDevice(deviceId);

      if (isConnected(snapshot.videoConnectionStatus)) {
        videoConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'video',
          ...resolveSourceMetadata(deviceId, 'video')
        });

        if (isStreamingReady(snapshot.videoStreamState)) {
          emitStreamingSuccess('video', {
            ...deviceAnalyticsContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'video',
            ...resolveStreamingStats(videoStats)
          });
          monitoredRef.current.video = true;
        }
      } else if (
        !isConnected(snapshot.videoConnectionStatus) &&
        isStreamingReady(snapshot.videoStreamState)
      ) {
        videoConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'video',
          ...resolveSourceMetadata(deviceId, 'video')
        });
        emitStreamingSuccess('video', {
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'video',
          ...resolveStreamingStats(videoStats)
        });
        monitoredRef.current.video = true;
      }

      if (isConnected(snapshot.audioConnectionStatus)) {
        audioConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'audio'
        });

        if (isStreamingReady(snapshot.audioStreamState)) {
          emitStreamingSuccess('audio', {
            ...deviceAnalyticsContext,
            device_id: deviceId,
            surface: ROOM_LIFECYCLE_SURFACE,
            media_type: 'audio',
            ...resolveStreamingStats(audioStats)
          });
          monitoredRef.current.audio = true;
        }
      } else if (
        !isConnected(snapshot.audioConnectionStatus) &&
        isStreamingReady(snapshot.audioStreamState)
      ) {
        audioConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'audio'
        });
        emitStreamingSuccess('audio', {
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'audio',
          ...resolveStreamingStats(audioStats)
        });
        monitoredRef.current.audio = true;
      }

      if (isConnected(snapshot.controlsConnectionStatus)) {
        controlsConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackControlsConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'controls',
          ...resolveSourceMetadata(deviceId, 'controls')
        });
      } else if (!isConnected(snapshot.controlsConnectionStatus) && device?.control?.config) {
        controlsConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackControlsConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'controls',
          ...resolveSourceMetadata(deviceId, 'controls')
        });
      }

      if (monitoredRef.current.video || monitoredRef.current.audio) {
        if (controllerRef.current) {
          startQualityPingTimer(controllerRef.current);
        }
      }

      return;
    }

    const previousConnection = previousConnectionRef.current;
    const previousStream = previousStreamRef.current;

    if (!isConnected(previousConnection.video) && isConnected(snapshot.videoConnectionStatus)) {
      videoConnectFiredRef.current = true;
      mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        ...resolveSourceMetadata(deviceId, 'video')
      });
    }

    // §B item 9: media_connect_error fires when a connection drops (was connected) OR
    // when it enters a hard FAILED state without ever connecting (an attempt that never
    // succeeded). The else-if guards against double-firing on a connected→FAILED drop.
    if (isConnected(previousConnection.video) && !isConnected(snapshot.videoConnectionStatus)) {
      videoConnectFiredRef.current = false;
      mediaControlsDiscoveryTracker.trackMediaConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.videoConnectionStatus),
        error_message: connectionStatusLabel(snapshot.videoConnectionStatus),
        ...connectionStatusProp(snapshot.videoConnectionStatus)
      });
    } else if (
      !isConnected(previousConnection.video) &&
      isConnectionError(snapshot.videoConnectionStatus) &&
      !isConnectionError(previousConnection.video)
    ) {
      mediaControlsDiscoveryTracker.trackMediaConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.videoConnectionStatus),
        error_message: connectionStatusLabel(snapshot.videoConnectionStatus),
        ...connectionStatusProp(snapshot.videoConnectionStatus)
      });
    }

    if (!isConnected(previousConnection.audio) && isConnected(snapshot.audioConnectionStatus)) {
      audioConnectFiredRef.current = true;
      mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio'
      });
    }

    if (isConnected(previousConnection.audio) && !isConnected(snapshot.audioConnectionStatus)) {
      audioConnectFiredRef.current = false;
      mediaControlsDiscoveryTracker.trackMediaConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.audioConnectionStatus),
        error_message: connectionStatusLabel(snapshot.audioConnectionStatus),
        ...connectionStatusProp(snapshot.audioConnectionStatus)
      });
    } else if (
      !isConnected(previousConnection.audio) &&
      isConnectionError(snapshot.audioConnectionStatus) &&
      !isConnectionError(previousConnection.audio)
    ) {
      mediaControlsDiscoveryTracker.trackMediaConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.audioConnectionStatus),
        error_message: connectionStatusLabel(snapshot.audioConnectionStatus),
        ...connectionStatusProp(snapshot.audioConnectionStatus)
      });
    }

    if (
      !isConnected(previousConnection.controls) &&
      isConnected(snapshot.controlsConnectionStatus)
    ) {
      controlsConnectFiredRef.current = true;
      mediaControlsDiscoveryTracker.trackControlsConnectSuccess({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'controls',
        ...resolveSourceMetadata(deviceId, 'controls')
      });
    }

    // §1.4.2: controls_connect_error is the inverse of controls_connect_success — it
    // matches its prop set (source metadata), adds error_type/error_message, and carries
    // the per-device prior-success flag (whether 1.4.1 fired before this attempt). It
    // fires on a connected→down drop OR a never-connected→FAILED attempt.
    const priorControlsConnection = previousConnection.controls;

    if (isConnected(priorControlsConnection) && !isConnected(snapshot.controlsConnectionStatus)) {
      const priorSuccess = controlsConnectFiredRef.current;
      controlsConnectFiredRef.current = false;
      mediaControlsDiscoveryTracker.trackControlsConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'controls',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.controlsConnectionStatus),
        error_message: connectionStatusLabel(snapshot.controlsConnectionStatus),
        ...connectionStatusProp(snapshot.controlsConnectionStatus),
        prior_success: priorSuccess,
        ...resolveSourceMetadata(deviceId, 'controls')
      });
    } else if (
      !isConnected(priorControlsConnection) &&
      isConnectionError(snapshot.controlsConnectionStatus) &&
      !isConnectionError(priorControlsConnection)
    ) {
      const priorSuccess = controlsConnectFiredRef.current;
      mediaControlsDiscoveryTracker.trackControlsConnectError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'controls',
        is_error_state: true,
        error_type: connectionErrorType(snapshot.controlsConnectionStatus),
        error_message: connectionStatusLabel(snapshot.controlsConnectionStatus),
        ...connectionStatusProp(snapshot.controlsConnectionStatus),
        prior_success: priorSuccess,
        ...resolveSourceMetadata(deviceId, 'controls')
      });
    }

    if (!isStreamingReady(previousStream.video) && isStreamingReady(snapshot.videoStreamState)) {
      if (!videoConnectFiredRef.current) {
        videoConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'video',
          ...resolveSourceMetadata(deviceId, 'video')
        });
      }
      emitStreamingSuccess('video', {
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        ...resolveStreamingStats(videoStats)
      });
      // Streaming success (re)opens the monitoring window for the quality ping.
      monitoredRef.current.video = true;
    }

    // §B item 9: media_streaming_error fires when a ready stream stops being ready OR
    // when it enters a hard FAIL state without ever being ready (cannot start/resume).
    if (isStreamingReady(previousStream.video) && !isStreamingReady(snapshot.videoStreamState)) {
      mediaControlsDiscoveryTracker.trackMediaStreamingError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        is_error_state: true,
        error_type: streamErrorType(snapshot.videoStreamState),
        error_message: streamStateLabel(snapshot.videoStreamState),
        ...connectionStatusProp(snapshot.videoConnectionStatus)
      });
    } else if (
      !isStreamingReady(previousStream.video) &&
      isStreamError(snapshot.videoStreamState) &&
      !isStreamError(previousStream.video)
    ) {
      mediaControlsDiscoveryTracker.trackMediaStreamingError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'video',
        is_error_state: true,
        error_type: streamErrorType(snapshot.videoStreamState),
        error_message: streamStateLabel(snapshot.videoStreamState),
        ...connectionStatusProp(snapshot.videoConnectionStatus)
      });
    }

    if (!isStreamingReady(previousStream.audio) && isStreamingReady(snapshot.audioStreamState)) {
      if (!audioConnectFiredRef.current) {
        audioConnectFiredRef.current = true;
        mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
          ...deviceAnalyticsContext,
          device_id: deviceId,
          surface: ROOM_LIFECYCLE_SURFACE,
          media_type: 'audio'
        });
      }
      emitStreamingSuccess('audio', {
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio',
        ...resolveStreamingStats(audioStats)
      });
      monitoredRef.current.audio = true;
    }

    if (isStreamingReady(previousStream.audio) && !isStreamingReady(snapshot.audioStreamState)) {
      mediaControlsDiscoveryTracker.trackMediaStreamingError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio',
        is_error_state: true,
        error_type: streamErrorType(snapshot.audioStreamState),
        error_message: streamStateLabel(snapshot.audioStreamState),
        ...connectionStatusProp(snapshot.audioConnectionStatus)
      });
    } else if (
      !isStreamingReady(previousStream.audio) &&
      isStreamError(snapshot.audioStreamState) &&
      !isStreamError(previousStream.audio)
    ) {
      mediaControlsDiscoveryTracker.trackMediaStreamingError({
        ...deviceAnalyticsContext,
        device_id: deviceId,
        surface: ROOM_LIFECYCLE_SURFACE,
        media_type: 'audio',
        is_error_state: true,
        error_type: streamErrorType(snapshot.audioStreamState),
        error_message: streamStateLabel(snapshot.audioStreamState),
        ...connectionStatusProp(snapshot.audioConnectionStatus)
      });
    }

    // Close the monitoring window on PERMANENT error only. Transient not-ready states
    // (CONNECTING / NO_PREROLL / PAUSED / ASYNC) keep the ping firing with is_error_state
    // set, per §1.3.5 "fires EVEN in error states ... UNTIL device removal or permanent
    // error".
    if (
      isStreamError(snapshot.videoStreamState) ||
      isConnectionError(snapshot.videoConnectionStatus)
    ) {
      monitoredRef.current.video = false;
    }

    if (
      isStreamError(snapshot.audioStreamState) ||
      isConnectionError(snapshot.audioConnectionStatus)
    ) {
      monitoredRef.current.audio = false;
    }

    // Run the single per-device timer while ANY channel is monitored; stop it once both
    // channels have permanently errored (device removal is handled in the null branch).
    if (controllerRef.current) {
      if (monitoredRef.current.video || monitoredRef.current.audio) {
        startQualityPingTimer(controllerRef.current);
      } else {
        stopQualityPingTimer(controllerRef.current);
      }
    }

    previousConnectionRef.current = {
      video: snapshot.videoConnectionStatus,
      audio: snapshot.audioConnectionStatus,
      controls: snapshot.controlsConnectionStatus
    };
    previousStreamRef.current = {
      video: snapshot.videoStreamState,
      audio: snapshot.audioStreamState
    };
  }, [deviceId, mediaControlsDiscoveryTracker, snapshot]);

  return null;
};

export const DeviceLifecycleAnalyticsObserver = ({
  roomId
}: {
  roomId: Room['id'];
}): ReactElement => {
  const deviceIds = useMultiDeviceStateQuery<{ id: Device['id'] }>({
    // Per-device value only; the hook keys the map by device id.
    transform: (device) => ({ id: device.id })
  });

  return (
    <>
      {Object.keys(deviceIds).map((unsafeDeviceId) => {
        const deviceId = unsafeDeviceId as Device['id'];

        // Preview devices use a tmp: id. They are not the committed camera, and
        // emitting media_stream_succeeded for them makes the journey test read
        // the preview as the first stream.
        if (isTransient(deviceId)) {
          return null;
        }

        // Room-scoped identity prevents lifecycle refs and timers leaking across navigation.
        return (
          <DeviceLifecycleAnalyticsObserverEntry
            deviceId={deviceId}
            key={`${roomId}:${deviceId}`}
            roomId={roomId}
          />
        );
      })}
    </>
  );
};
